"use client";
/** Cele două ecrane publice de recuperare a parolei: „cere link” și „setează parola nouă”. */
import { useState, useTransition } from "react";
import Link from "next/link";
import { I, SEL } from "@/components/ui/icons";
import { requestPasswordResetAction, resetPasswordAction } from "@/app/login/actions";
import { CaptchaField } from "@/components/auth/captcha-field";

const BTN = "inline-flex items-center justify-center gap-2 rounded-[var(--l-r-ctl)] bg-accent px-4 py-2.5 text-[14px] font-semibold text-white shadow-card transition enabled:hover:bg-accent-ink disabled:opacity-60";
const Spinner = () => <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />;
const Err = ({ msg }: { msg: string }) => (
  <p className="flex items-start gap-1.5 rounded-[var(--l-r-xs)] bg-neg-soft px-3 py-2 text-[12.5px] font-medium text-neg"><I name="alert" className="!size-4 shrink-0" />{msg}</p>
);

export function RequestResetForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [capToken, setCapToken] = useState("");
  const [capCode, setCapCode] = useState("");
  const [capReload, setCapReload] = useState(0);
  const [pending, start] = useTransition();

  const submit = () => {
    if (!capCode) { setError("Scrie și codul din imagine."); return; }
    setError("");
    start(async () => {
      const r = await requestPasswordResetAction(email.trim(), { token: capToken, code: capCode });
      /* Jetonul CAPTCHA e de unică folosință — la eroare cerem o imagine nouă. */
      if ("error" in r) { setError(r.error); setCapReload((n) => n + 1); return; }
      setSent(true);
    });
  };

  /* Confirmarea e IDENTICĂ fie că adresa are cont, fie că nu — altfel ecranul ăsta ar
     spune oricui dacă un anumit om lucrează la firma respectivă. */
  if (sent) {
    return (
      <div className="rounded-[14px] border border-line bg-surface p-6 shadow-card">
        <p className="font-display text-[19px] font-medium text-ink">Verifică-ți emailul</p>
        <p className="mt-2 text-[13px] leading-6 text-ink-2">
          Dacă <strong className="text-ink">{email.trim()}</strong> are un cont activ, ți-am trimis un link de resetare.
          E valabil o oră. Verifică și în „Spam”, dacă nu apare în câteva minute.
        </p>
        <Link href="/login" className="mt-5 block text-center text-[12.5px] font-semibold text-accent hover:underline">Înapoi la autentificare</Link>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-line bg-surface p-6 shadow-card">
      <p className="font-display text-[19px] font-medium text-ink">Recuperare parolă</p>
      <p className="mt-1 text-[13px] leading-5 text-ink-3">Scrie adresa cu care intri în cont și îți trimitem un link de resetare.</p>
      <form className="mt-5 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-ink-2">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" autoFocus className={`${SEL} w-full`} />
        </div>
        <CaptchaField code={capCode} onCode={setCapCode} onToken={setCapToken} reload={capReload} />
        {error && <Err msg={error} />}
        <button type="submit" disabled={pending || !email.includes("@")} className={BTN}>
          {pending ? <Spinner /> : <I name="mail" className="!size-4" />}Trimite linkul
        </button>
      </form>
      <Link href="/login" className="mt-4 block text-center text-[12.5px] font-medium text-ink-3 transition hover:text-ink-2">Înapoi la autentificare</Link>
    </div>
  );
}

export function SetNewPasswordForm({ token, temporara = false }: { token: string; temporara?: boolean }) {
  const [f, setF] = useState({ next: "", repeat: "" });
  const [error, setError] = useState("");
  const [pending, start] = useTransition();

  const submit = () => {
    if (f.next.length < 8) { setError("Parola trebuie să aibă minim 8 caractere."); return; }
    if (f.next !== f.repeat) { setError("Cele două parole nu coincid."); return; }
    setError("");
    start(async () => {
      const r = await resetPasswordAction(token, f.next);
      if (r?.error) setError(r.error);
    });
  };

  return (
    <div className="rounded-[14px] border border-line bg-surface p-6 shadow-card">
      <p className="font-display text-[19px] font-medium text-ink">{temporara ? "Alege-ți parola" : "Parolă nouă"}</p>
      <p className="mt-1 text-[13px] leading-5 text-ink-3">
        {temporara
          ? "Ai intrat cu o parolă temporară primită de la administrator. Alege acum parola ta, apoi autentifică-te cu ea."
          : "După salvare, toate dispozitivele conectate la cont sunt deconectate."}
      </p>
      <form className="mt-5 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-ink-2">Parola nouă</label>
          <input value={f.next} onChange={(e) => setF((s) => ({ ...s, next: e.target.value }))} type="password" autoComplete="new-password" autoFocus className={`${SEL} w-full`} />
        </div>
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-ink-2">Repetă parola</label>
          <input value={f.repeat} onChange={(e) => setF((s) => ({ ...s, repeat: e.target.value }))} type="password" autoComplete="new-password" className={`${SEL} w-full`} />
        </div>
        {error && <Err msg={error} />}
        <button type="submit" disabled={pending || !f.next} className={BTN}>
          {pending ? <Spinner /> : <I name="check" className="!size-4" />}Salvează parola
        </button>
      </form>
      <Link href="/login" className="mt-4 block text-center text-[12.5px] font-medium text-ink-3 transition hover:text-ink-2">Înapoi la autentificare</Link>
    </div>
  );
}
